import { FC, useEffect, useState, ReactNode } from 'react';
import { DashboardLayout } from '@components/layout/DashboardLayout';
import { Spinner } from '@components/common/Spinner';
import { adminService, AdminUser, AdminStats, AppSettings } from '@services/admin.service';
import { UserRole } from '@/types/index';

const ROLE_OPTIONS: UserRole[] = ['FREEMIUM', 'PREMIUM', 'PRO', 'ADMIN'];

const ROLE_COLORS: Record<UserRole, string> = {
  FREEMIUM: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  PREMIUM: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  PRO: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  ADMIN: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

export const Admin: FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [refreshingCommunes, setRefreshingCommunes] = useState(false);
  const [communesResult, setCommunesResult] = useState<{ created: number; updated: number; total: number } | null>(null);
  const [checkingOffMarket, setCheckingOffMarket] = useState(false);
  const [offMarketResult, setOffMarketResult] = useState<{ checked: number; markedOffMarket: number; scraperUnreachable: boolean } | null>(null);

  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [trialDaysInput, setTrialDaysInput] = useState<number>(30);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [u, s, cfg] = await Promise.all([
        adminService.listUsers(),
        adminService.getStats(),
        adminService.getSettings(),
      ]);
      setUsers(u);
      setStats(s);
      setSettings(cfg);
      setTrialDaysInput(cfg.trialDays);
    } catch {
      setError('Impossible de charger les données admin.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    setSettingsSaved(false);
    try {
      const updated = await adminService.updateSettings(trialDaysInput);
      setSettings(updated);
      setTrialDaysInput(updated.trialDays);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch {
      alert('Erreur lors de la sauvegarde des paramètres.');
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRefreshCommunes = async () => {
    setRefreshingCommunes(true);
    setCommunesResult(null);
    try {
      const result = await adminService.refreshCommunes();
      setCommunesResult(result);
    } catch {
      alert('Erreur lors de l\'actualisation des communes.');
    } finally {
      setRefreshingCommunes(false);
    }
  };

  const handleCheckOffMarket = async () => {
    setCheckingOffMarket(true);
    setOffMarketResult(null);
    try {
      const result = await adminService.checkOffMarket();
      setOffMarketResult(result);
    } catch {
      alert('Erreur lors de la vérification des annonces.');
    } finally {
      setCheckingOffMarket(false);
    }
  };

  const handleRoleChange = async (userId: string, role: UserRole) => {
    setUpdatingId(userId);
    try {
      const updated = await adminService.updateUserRole(userId, role);
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: updated.role } : u)));
    } catch {
      alert('Erreur lors du changement de plan.');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Administration</h1>

        {/* Paramètres d'inscription */}
        {settings !== null && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Paramètres d'inscription
            </h2>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  Période d'essai PREMIUM :
                </label>
                <input
                  type="number"
                  min={0}
                  max={3650}
                  value={trialDaysInput}
                  onChange={(e) => setTrialDaysInput(Math.max(0, Math.min(3650, parseInt(e.target.value) || 0)))}
                  className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">jours</span>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {trialDaysInput === 0 ? 'PREMIUM permanent (sans expiration)' : `Expiration ${trialDaysInput} jours après l'inscription`}
              </p>
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                {savingSettings ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              {settingsSaved && (
                <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">Enregistré</span>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {(['FREEMIUM', 'PREMIUM', 'PRO', 'ADMIN'] as UserRole[]).map((role) => (
              <div key={role} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.byRole[role]}</p>
                <p className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold mt-1 ${ROLE_COLORS[role]}`}>{role}</p>
              </div>
            ))}
          </div>
        )}

        {/* Données socio-démographiques */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Données socio-démographiques
          </h2>
          <div className="flex items-center gap-4 flex-wrap">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Actualise la table des communes (population OFS, chômage stats.swiss). Cron automatique le 1er février.
            </p>
            <button
              onClick={handleRefreshCommunes}
              disabled={refreshingCommunes}
              className="px-4 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg transition-colors whitespace-nowrap"
            >
              {refreshingCommunes ? 'Actualisation…' : 'Actualiser les communes'}
            </button>
            {communesResult && (
              <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                {communesResult.total} communes — {communesResult.created} créées, {communesResult.updated} mises à jour
              </span>
            )}
          </div>
        </div>

        {/* Vérification off-market */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Disponibilité des annonces
          </h2>
          <div className="flex items-center gap-4 flex-wrap">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Vérifie chaque annonce publique active via le scraper et marque les absentes off-market. Cron automatique chaque nuit à 2h.
            </p>
            <button
              onClick={handleCheckOffMarket}
              disabled={checkingOffMarket}
              className="px-4 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white rounded-lg transition-colors whitespace-nowrap"
            >
              {checkingOffMarket ? 'Vérification en cours…' : 'Vérifier maintenant'}
            </button>
            {offMarketResult && (
              <span className={`text-sm font-medium ${offMarketResult.scraperUnreachable ? 'text-red-500' : 'text-orange-600 dark:text-orange-400'}`}>
                {offMarketResult.scraperUnreachable
                  ? 'Scraper inaccessible — vérification impossible'
                  : `${offMarketResult.checked} annonces vérifiées — ${offMarketResult.markedOffMarket} passées off-market`}
              </span>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Utilisateurs ({users.length})
            </h2>
            <button
              onClick={load}
              className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              Actualiser
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : error ? (
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-left">
                    <th className="pb-3 pr-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Email</th>
                    <th className="pb-3 pr-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Nom</th>
                    <th className="pb-3 pr-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Biens</th>
                    <th className="pb-3 pr-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Inscrit le</th>
                    <th className="pb-3 pr-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Plan</th>
                    <th className="pb-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Expiration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {users.map((u) => {
                    let trialBadge: ReactNode =<span className="text-gray-400 text-xs">—</span>;
                    if (u.planExpiresAt) {
                      const daysLeft = Math.ceil((new Date(u.planExpiresAt).getTime() - Date.now()) / 86400000);
                      if (daysLeft <= 0) {
                        trialBadge = (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                            Expiré
                          </span>
                        );
                      } else {
                        const color = daysLeft <= 7
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                          : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
                        trialBadge = (
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
                            {daysLeft} j. restants
                          </span>
                        );
                      }
                    }
                    return (
                    <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td className="py-3 pr-4 text-gray-900 dark:text-gray-100 font-medium">{u.email}</td>
                      <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                        {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.name || '—'}
                      </td>
                      <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">{u.propertyCount}</td>
                      <td className="py-3 pr-4 text-gray-500 dark:text-gray-500 text-xs">
                        {new Date(u.createdAt).toLocaleDateString('fr-CH')}
                      </td>
                      <td className="py-3 pr-4">
                        <select
                          value={u.role}
                          disabled={updatingId === u.id}
                          onChange={(e) => handleRoleChange(u.id, e.target.value as UserRole)}
                          className={`text-xs font-semibold px-2 py-1 rounded-lg border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${ROLE_COLORS[u.role]}`}
                        >
                          {ROLE_OPTIONS.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-3">{trialBadge}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};
